"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Plus, MessageSquare, Edit2, Trash2, MoreVerticalIcon, Loader2, ImagePlus, Film, Pin, PinOff, Users, ChevronUp, LogOut, ExternalLink, Search, PenTool } from "lucide-react";
// import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { toast } from "sonner";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useChats, useDeleteChat, useUpdateChat, type Chat } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { ChatSearchDialog } from "./chat-search-dialog";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
// import { ProjectSwitcher } from "./project-switcher";

import type { Organization, Project } from "@/lib/types";

// const OrganizationSwitcher = dynamic(
// 	() => import("./organization-switcher").then((m) => m.OrganizationSwitcher),
// 	{ ssr: false },
// );

export interface ChatSidebarHandle {
  scrollToTop: () => void;
}
interface ChatSidebarProps {
  currentChatId?: string;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  className?: string;
  isLoading?: boolean;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  onOrganizationCreated: (organization: Organization) => void;
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  onProjectCreated: (project: Project) => void;
}
type ChatHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_CHAT = 52;
interface ChatHistoryRowProps {
  rows: ChatHistoryRow[];
  currentChatId?: string;
  editingId: string | null;
  editTitle: string;
  pendingFocusChatId: string | null;
  isPageLoading: boolean;
  isMobile: boolean;
  onChatSelect?: (chatId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveTitle: (chatId: string) => void;
  onCancelEdit: () => void;
  onDeleteChat: (chatId: string) => void;
  onTogglePin: (chat: Chat) => void;
  onStartEdit: (chat: Chat) => void;
  onEditFocused: () => void;
}
function getChatHistoryRowHeight(index: number, {
  rows
}: ChatHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_CHAT;
}
function EditChatTitleInput(t0) {
  const $ = _c(16);
  const {
    chatId,
    value,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatId || $[7] !== onSave) {
    t4 = () => onSave(chatId);
    $[6] = chatId;
    $[7] = onSave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[9] = onCancel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
type ChatHistoryItemProps = Pick<ChatHistoryRowProps, "currentChatId" | "editingId" | "editTitle" | "pendingFocusChatId" | "isPageLoading" | "isMobile" | "onChatSelect" | "onEditTitleChange" | "onSaveTitle" | "onCancelEdit" | "onDeleteChat" | "onTogglePin" | "onStartEdit" | "onEditFocused"> & {
  chat: Chat;
};
function ChatHistoryItem(t0) {
  const $ = _c(26);
  const {
    chat,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const isEditing = editingId === chat.id;
  const isActive = currentChatId === chat.id;
  const isActionFocusable = isActive && isMobile ? undefined : -1;
  let t1;
  if ($[0] !== chat.id || $[1] !== chat.messageCount || $[2] !== chat.title || $[3] !== chat.updatedAt || $[4] !== editTitle || $[5] !== isActive || $[6] !== isEditing || $[7] !== isPageLoading || $[8] !== onCancelEdit || $[9] !== onChatSelect || $[10] !== onEditFocused || $[11] !== onEditTitleChange || $[12] !== onSaveTitle || $[13] !== pendingFocusChatId) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditChatTitleInput chatId={chat.id} value={editTitle} shouldFocus={pendingFocusChatId === chat.id} onChange={onEditTitleChange} onSave={onSaveTitle} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton isActive={isActive} onClick={() => onChatSelect?.(chat.id)} className={["h-full! w-full justify-start group relative pr-2 !transition-none group-hover/chat-row:pr-[4.5rem]", isActive ? "max-md:pr-[4.5rem]" : ""].join(" ")} type="button" disabled={isPageLoading}><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{chat.title}</div><div className="text-xs text-muted-foreground">{chat.messageCount} messages • {formatDate(chat.updatedAt)}</div></div></SidebarMenuButton>;
    $[0] = chat.id;
    $[1] = chat.messageCount;
    $[2] = chat.title;
    $[3] = chat.updatedAt;
    $[4] = editTitle;
    $[5] = isActive;
    $[6] = isEditing;
    $[7] = isPageLoading;
    $[8] = onCancelEdit;
    $[9] = onChatSelect;
    $[10] = onEditFocused;
    $[11] = onEditTitleChange;
    $[12] = onSaveTitle;
    $[13] = pendingFocusChatId;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== chat || $[16] !== isActionFocusable || $[17] !== isActive || $[18] !== isEditing || $[19] !== onDeleteChat || $[20] !== onStartEdit || $[21] !== onTogglePin) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><button type="button" aria-label={chat.pinned ? "Unpin chat" : "Pin chat"} title={chat.pinned ? "Unpin chat" : "Pin chat"} tabIndex={isActionFocusable} onClick={e => {
        e.stopPropagation();
        onTogglePin(chat);
      }} className={["flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground outline-hidden transition-opacity hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring", "pointer-events-none opacity-0 group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100", isActive ? "max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}>{chat.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" tabIndex={isActionFocusable} onClick={_temp} className={["pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/chat-row:flex group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100", isActive ? "max-md:flex max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onTogglePin(chat);
          }} className="flex items-center gap-2">{chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}{chat.pinned ? "Unpin" : "Pin"}</DropdownMenuItem><DropdownMenuItem onClick={e_2 => {
            e_2.stopPropagation();
            onStartEdit(chat);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_3 => {
            e_3.stopPropagation();
            onDeleteChat(chat.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[15] = chat;
    $[16] = isActionFocusable;
    $[17] = isActive;
    $[18] = isEditing;
    $[19] = onDeleteChat;
    $[20] = onStartEdit;
    $[21] = onTogglePin;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== t1 || $[24] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/chat-row relative h-full">{t1}{t2}</div></div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
function _temp(e_0) {
  e_0.stopPropagation();
}
function ChatHistoryRowComponent(t0) {
  const $ = _c(28);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== currentChatId || $[9] !== editTitle || $[10] !== editingId || $[11] !== isMobile || $[12] !== isPageLoading || $[13] !== onCancelEdit || $[14] !== onChatSelect || $[15] !== onDeleteChat || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onSaveTitle || $[19] !== onStartEdit || $[20] !== onTogglePin || $[21] !== pendingFocusChatId || $[22] !== row.chat) {
    t1 = <ChatHistoryItem chat={row.chat} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={onChatSelect} onEditTitleChange={onEditTitleChange} onSaveTitle={onSaveTitle} onCancelEdit={onCancelEdit} onDeleteChat={onDeleteChat} onTogglePin={onTogglePin} onStartEdit={onStartEdit} onEditFocused={onEditFocused} />;
    $[8] = currentChatId;
    $[9] = editTitle;
    $[10] = editingId;
    $[11] = isMobile;
    $[12] = isPageLoading;
    $[13] = onCancelEdit;
    $[14] = onChatSelect;
    $[15] = onDeleteChat;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onSaveTitle;
    $[19] = onStartEdit;
    $[20] = onTogglePin;
    $[21] = pendingFocusChatId;
    $[22] = row.chat;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== ariaAttributes || $[25] !== style || $[26] !== t1) {
    t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
    $[24] = ariaAttributes;
    $[25] = style;
    $[26] = t1;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  return t2;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupChatsByDate(chats: Chat[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
export const ChatSidebar = function ChatSidebar(t0) {
  const $ = _c(224);
  const {
    ref,
    currentChatId,
    onChatSelect,
    onNewChat,
    className,
    isLoading: t1,
    organizations,
    selectedOrganization,
    onSelectOrganization
  } = t0;
  const isPageLoading = t1 === undefined ? false : t1;
  const listContainerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      scrollToTop: () => {
        const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
        scrollEl?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useImperativeHandle(ref, t2);
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[1] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[1] = searchParams;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const orgIdParam = t3;
  let t4;
  if ($[3] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[3] = orgIdParam;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const withOrg = t4;
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile,
    setOpenMobile
  } = useSidebar();
  const showOrganizationSwitcher = pathname === "/" || pathname === "/group";
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const resolvedOrgId = selectedOrganization?.id ?? organization?.id;
  const {
    data: chatsData,
    isLoading: isChatsLoading
  } = useChats(resolvedOrgId);
  const deleteChat = useDeleteChat();
  const updateChat = useUpdateChat();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [pendingFocusChatId, setPendingFocusChatId] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  chatsData?.chats;
  let t5;
  if ($[5] !== chatsData?.chats) {
    t5 = chatsData?.chats ?? [];
    $[5] = chatsData?.chats;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const chats = t5;
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t6;
  if ($[7] !== currentTheme || $[8] !== setTheme) {
    t6 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[7] = currentTheme;
    $[8] = setTheme;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const toggleTheme = t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(window.navigator.platform));
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== isMac) {
    t9 = () => {
      const handleKeyDown = event => {
        const key = event.key?.toLowerCase();
        const isSearchShortcut = isMac ? event.metaKey && key === "k" && !event.altKey && !event.ctrlKey : event.altKey && key === "k" && !event.metaKey && !event.ctrlKey;
        if (!isSearchShortcut || event.shiftKey || event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        setIsSearchOpen(true);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t10 = [isMac];
    $[12] = isMac;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== posthog || $[16] !== queryClient || $[17] !== router || $[18] !== signOut) {
    t11 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {
        toast.error("Failed to clear last used project cookies");
      }
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://llm-playground.agenzo.com/login");
          }
        }
      });
    };
    $[15] = posthog;
    $[16] = queryClient;
    $[17] = router;
    $[18] = signOut;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const logout = t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = chat => {
      setEditingId(chat.id);
      setEditTitle(chat.title);
      setPendingFocusChatId(chat.id);
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const handleEditTitle = t12;
  let t13;
  if ($[21] !== chats || $[22] !== editTitle || $[23] !== updateChat) {
    t13 = chatId => {
      const nextTitle = editTitle.trim();
      const currentTitle = chats.find(chat_0 => chat_0.id === chatId)?.title.trim();
      if (nextTitle && nextTitle !== currentTitle) {
        updateChat.mutate({
          params: {
            path: {
              id: chatId
            }
          },
          body: {
            title: nextTitle
          }
        });
      }
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[21] = chats;
    $[22] = editTitle;
    $[23] = updateChat;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const saveTitle = t13;
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const cancelEditTitle = t14;
  let t15;
  if ($[26] !== currentChatId || $[27] !== deleteChat || $[28] !== onNewChat) {
    t15 = chatId_0 => {
      deleteChat.mutate({
        params: {
          path: {
            id: chatId_0
          }
        }
      });
      if (currentChatId === chatId_0) {
        onNewChat?.();
      }
    };
    $[26] = currentChatId;
    $[27] = deleteChat;
    $[28] = onNewChat;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleDeleteChat = t15;
  let t16;
  if ($[30] !== isMobile || $[31] !== onChatSelect || $[32] !== setOpenMobile) {
    t16 = chatId_1 => {
      onChatSelect?.(chatId_1);
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[30] = isMobile;
    $[31] = onChatSelect;
    $[32] = setOpenMobile;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const handleChatSelect = t16;
  let t17;
  if ($[34] !== updateChat) {
    t17 = chat_1 => {
      updateChat.mutate({
        params: {
          path: {
            id: chat_1.id
          }
        },
        body: {
          pinned: !chat_1.pinned
        }
      });
    };
    $[34] = updateChat;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const handleTogglePin = t17;
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setPendingFocusChatId(null);
    };
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const onEditFocused = t18;
  let t19;
  if ($[37] !== chats) {
    t19 = [...chats].sort(_temp2);
    $[37] = chats;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const sortedChats = t19;
  let t20;
  if ($[39] !== sortedChats) {
    t20 = sortedChats.filter(_temp3);
    $[39] = sortedChats;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const pinnedChats = t20;
  let t21;
  if ($[41] !== sortedChats) {
    t21 = groupChatsByDate(sortedChats.filter(_temp4));
    $[41] = sortedChats;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const chatGroups = t21;
  let rows;
  if ($[43] !== chatGroups.lastWeek || $[44] !== chatGroups.older || $[45] !== chatGroups.today || $[46] !== chatGroups.yesterday) {
    const groups = [{
      title: "Today",
      chats: chatGroups.today
    }, {
      title: "Yesterday",
      chats: chatGroups.yesterday
    }, {
      title: "Last 7 days",
      chats: chatGroups.lastWeek
    }, {
      title: "Older",
      chats: chatGroups.older
    }];
    rows = [];
    groups.forEach((t22, groupIndex) => {
      const {
        title,
        chats: groupedChats
      } = t22;
      if (groupedChats.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat_4 => {
        rows.push({
          type: "chat",
          key: `chat-${chat_4.id}`,
          chat: chat_4
        });
      });
      const hasNextGroupWithChats = groups.slice(groupIndex + 1).some(_temp5);
      if (hasNextGroupWithChats) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[43] = chatGroups.lastWeek;
    $[44] = chatGroups.older;
    $[45] = chatGroups.today;
    $[46] = chatGroups.yesterday;
    $[47] = rows;
  } else {
    rows = $[47];
  }
  const historyRows = rows;
  let t22;
  if ($[48] !== currentChatId || $[49] !== editTitle || $[50] !== editingId || $[51] !== handleChatSelect || $[52] !== handleDeleteChat || $[53] !== handleTogglePin || $[54] !== historyRows || $[55] !== isMobile || $[56] !== isPageLoading || $[57] !== pendingFocusChatId || $[58] !== saveTitle) {
    t22 = {
      rows: historyRows,
      currentChatId,
      editingId,
      editTitle,
      pendingFocusChatId,
      isPageLoading,
      isMobile,
      onChatSelect: handleChatSelect,
      onEditTitleChange: setEditTitle,
      onSaveTitle: saveTitle,
      onCancelEdit: cancelEditTitle,
      onDeleteChat: handleDeleteChat,
      onTogglePin: handleTogglePin,
      onStartEdit: handleEditTitle,
      onEditFocused
    };
    $[48] = currentChatId;
    $[49] = editTitle;
    $[50] = editingId;
    $[51] = handleChatSelect;
    $[52] = handleDeleteChat;
    $[53] = handleTogglePin;
    $[54] = historyRows;
    $[55] = isMobile;
    $[56] = isPageLoading;
    $[57] = pendingFocusChatId;
    $[58] = saveTitle;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const rowProps = t22;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t23;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[60] = t23;
    } else {
      t23 = $[60];
    }
    return t23;
  }
  if (!isAuthenticated) {
    let t23;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="size-6" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Chat</Badge></Link>;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    let t24;
    let t25;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <div className="font-medium mb-2">Sign in required</div>;
      t25 = <p className="text-muted-foreground mb-3">Please sign in to view organizations, projects, and chats.</p>;
      $[62] = t24;
      $[63] = t25;
    } else {
      t24 = $[62];
      t25 = $[63];
    }
    let t26;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[64] = t26;
    } else {
      t26 = $[64];
    }
    let t27;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t23}<div className="w-full rounded-md border p-4 text-sm">{t24}{t25}<div className="flex items-center justify-end gap-2">{t26}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div></SidebarHeader>;
      $[65] = t27;
    } else {
      t27 = $[65];
    }
    let t28;
    if ($[66] !== className) {
      t28 = <Sidebar className={className}>{t27}</Sidebar>;
      $[66] = className;
      $[67] = t28;
    } else {
      t28 = $[67];
    }
    return t28;
  }
  if (isChatsLoading || isOrgLoading) {
    let t23;
    if ($[68] !== isOrgLoading || $[69] !== selectedOrganization) {
      t23 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[68] = isOrgLoading;
      $[69] = selectedOrganization;
      $[70] = t23;
    } else {
      t23 = $[70];
    }
    return t23;
  }
  const t23 = className + " max-md:hidden";
  let t24;
  if ($[71] !== withOrg) {
    t24 = withOrg("/");
    $[71] = withOrg;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  let t26;
  let t27;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t26 = <span className="text-lg font-bold tracking-tight">LLM Gateway</span>;
    t27 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">Chat</Badge>;
    $[73] = t25;
    $[74] = t26;
    $[75] = t27;
  } else {
    t25 = $[73];
    t26 = $[74];
    t27 = $[75];
  }
  let t28;
  if ($[76] !== t24) {
    t28 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="LLM Gateway"><Link href={t24} prefetch={true}>{t25}{t26}{t27}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[76] = t24;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== isPageLoading) {
    t29 = isPageLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />;
    $[78] = isPageLoading;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span>New Chat</span>;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== isPageLoading || $[82] !== onNewChat || $[83] !== t29) {
    t31 = <SidebarMenuItem><SidebarMenuButton onClick={onNewChat} disabled={isPageLoading} tooltip="New Chat" className="border border-border">{t29}{t30}</SidebarMenuButton></SidebarMenuItem>;
    $[81] = isPageLoading;
    $[82] = onNewChat;
    $[83] = t29;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  let t33;
  let t34;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsSearchOpen(true);
    t33 = <Search className="h-4 w-4" />;
    t34 = <span>Search Chats</span>;
    $[85] = t32;
    $[86] = t33;
    $[87] = t34;
  } else {
    t32 = $[85];
    t33 = $[86];
    t34 = $[87];
  }
  const t35 = isMac ? "\u2318K" : "Alt+K";
  let t36;
  if ($[88] !== t35) {
    t36 = <SidebarMenuItem><SidebarMenuButton type="button" tooltip="Search Chats" onClick={t32}>{t33}{t34}<kbd className="ml-auto text-xs font-medium text-muted-foreground opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 group-data-[collapsible=icon]:hidden">{t35}</kbd></SidebarMenuButton></SidebarMenuItem>;
    $[88] = t35;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  const t37 = pathname === "/";
  let t38;
  if ($[90] !== withOrg) {
    t38 = withOrg("/");
    $[90] = withOrg;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  let t40;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <MessageSquare className="h-4 w-4" />;
    t40 = <span>Chat</span>;
    $[92] = t39;
    $[93] = t40;
  } else {
    t39 = $[92];
    t40 = $[93];
  }
  let t41;
  if ($[94] !== t38) {
    t41 = <Link href={t38} prefetch={true}>{t39}{t40}</Link>;
    $[94] = t38;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[96] !== t37 || $[97] !== t41) {
    t42 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Chat" isActive={t37}>{t41}</SidebarMenuButton></SidebarMenuItem>;
    $[96] = t37;
    $[97] = t41;
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  const t43 = pathname === "/group";
  let t44;
  if ($[99] !== withOrg) {
    t44 = withOrg("/group");
    $[99] = withOrg;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  let t45;
  let t46;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Users className="h-4 w-4" />;
    t46 = <span>Group Chat</span>;
    $[101] = t45;
    $[102] = t46;
  } else {
    t45 = $[101];
    t46 = $[102];
  }
  let t47;
  if ($[103] !== t44) {
    t47 = <Link href={t44} prefetch={true}>{t45}{t46}</Link>;
    $[103] = t44;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== t43 || $[106] !== t47) {
    t48 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Group Chat" isActive={t43}>{t47}</SidebarMenuButton></SidebarMenuItem>;
    $[105] = t43;
    $[106] = t47;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  const t49 = pathname === "/image";
  let t50;
  if ($[108] !== withOrg) {
    t50 = withOrg("/image");
    $[108] = withOrg;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  let t52;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <ImagePlus className="h-4 w-4" />;
    t52 = <span>Image Studio</span>;
    $[110] = t51;
    $[111] = t52;
  } else {
    t51 = $[110];
    t52 = $[111];
  }
  let t53;
  if ($[112] !== t50) {
    t53 = <Link href={t50} prefetch={true}>{t51}{t52}</Link>;
    $[112] = t50;
    $[113] = t53;
  } else {
    t53 = $[113];
  }
  let t54;
  if ($[114] !== t49 || $[115] !== t53) {
    t54 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Image Studio" isActive={t49}>{t53}</SidebarMenuButton></SidebarMenuItem>;
    $[114] = t49;
    $[115] = t53;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  const t55 = pathname === "/video";
  let t56;
  if ($[117] !== withOrg) {
    t56 = withOrg("/video");
    $[117] = withOrg;
    $[118] = t56;
  } else {
    t56 = $[118];
  }
  let t57;
  let t58;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Film className="h-4 w-4" />;
    t58 = <span>Video Studio</span>;
    $[119] = t57;
    $[120] = t58;
  } else {
    t57 = $[119];
    t58 = $[120];
  }
  let t59;
  if ($[121] !== t56) {
    t59 = <Link href={t56} prefetch={true}>{t57}{t58}</Link>;
    $[121] = t56;
    $[122] = t59;
  } else {
    t59 = $[122];
  }
  let t60;
  if ($[123] !== t55 || $[124] !== t59) {
    t60 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Video Studio" isActive={t55}>{t59}</SidebarMenuButton></SidebarMenuItem>;
    $[123] = t55;
    $[124] = t59;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  const t61 = pathname === "/canvas";
  let t62;
  if ($[126] !== withOrg) {
    t62 = withOrg("/canvas");
    $[126] = withOrg;
    $[127] = t62;
  } else {
    t62 = $[127];
  }
  let t63;
  let t64;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <PenTool className="h-4 w-4" />;
    t64 = <span>Canvas</span>;
    $[128] = t63;
    $[129] = t64;
  } else {
    t63 = $[128];
    t64 = $[129];
  }
  let t65;
  if ($[130] !== t62) {
    t65 = <Link href={t62} prefetch={true}>{t63}{t64}</Link>;
    $[130] = t62;
    $[131] = t65;
  } else {
    t65 = $[131];
  }
  let t66;
  if ($[132] !== t61 || $[133] !== t65) {
    t66 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Canvas" isActive={t61}>{t65}</SidebarMenuButton></SidebarMenuItem>;
    $[132] = t61;
    $[133] = t65;
    $[134] = t66;
  } else {
    t66 = $[134];
  }
  let t67;
  if ($[135] !== t28 || $[136] !== t31 || $[137] !== t36 || $[138] !== t42 || $[139] !== t48 || $[140] !== t54 || $[141] !== t60 || $[142] !== t66) {
    t67 = <SidebarHeader><SidebarMenu>{t28}{t31}{t36}{t42}{t48}{t54}{t60}{t66}</SidebarMenu></SidebarHeader>;
    $[135] = t28;
    $[136] = t31;
    $[137] = t36;
    $[138] = t42;
    $[139] = t48;
    $[140] = t54;
    $[141] = t60;
    $[142] = t66;
    $[143] = t67;
  } else {
    t67 = $[143];
  }
  let t68;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[144] = t68;
  } else {
    t68 = $[144];
  }
  let t69;
  if ($[145] !== onSelectOrganization || $[146] !== organizations || $[147] !== selectedOrganization || $[148] !== showOrganizationSwitcher) {
    t69 = organizations.length > 0 && showOrganizationSwitcher ? <><SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu></> : null;
    $[145] = onSelectOrganization;
    $[146] = organizations;
    $[147] = selectedOrganization;
    $[148] = showOrganizationSwitcher;
    $[149] = t69;
  } else {
    t69 = $[149];
  }
  let t70;
  if ($[150] !== t69) {
    t70 = <div>{t68}{t69}</div>;
    $[150] = t69;
    $[151] = t70;
  } else {
    t70 = $[151];
  }
  let t71;
  if ($[152] !== chats.length || $[153] !== currentChatId || $[154] !== editTitle || $[155] !== editingId || $[156] !== handleChatSelect || $[157] !== handleDeleteChat || $[158] !== handleTogglePin || $[159] !== historyRows || $[160] !== isMobile || $[161] !== isPageLoading || $[162] !== pendingFocusChatId || $[163] !== pinnedChats || $[164] !== rowProps || $[165] !== saveTitle) {
    t71 = chats.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No chat history</p><p className="text-xs text-muted-foreground">Start a new conversation to see it here</p></div> : <>{pinnedChats.length > 0 && <div className="shrink-0 max-h-[45%] overflow-y-auto border-b border-sidebar-border/60 pb-1"><div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">Pinned</div>{pinnedChats.map(chat_5 => <div key={chat_5.id} style={{
          height: ROW_HEIGHT_CHAT
        }}><ChatHistoryItem chat={chat_5} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={handleChatSelect} onEditTitleChange={setEditTitle} onSaveTitle={saveTitle} onCancelEdit={cancelEditTitle} onDeleteChat={handleDeleteChat} onTogglePin={handleTogglePin} onStartEdit={handleEditTitle} onEditFocused={onEditFocused} /></div>)}</div>}{historyRows.length > 0 && <List className="min-h-0 w-full flex-1" style={{
        width: "100%"
      }} rowComponent={ChatHistoryRowComponent} rowCount={historyRows.length} rowHeight={getChatHistoryRowHeight} rowProps={rowProps} overscanCount={8} />}</>;
    $[152] = chats.length;
    $[153] = currentChatId;
    $[154] = editTitle;
    $[155] = editingId;
    $[156] = handleChatSelect;
    $[157] = handleDeleteChat;
    $[158] = handleTogglePin;
    $[159] = historyRows;
    $[160] = isMobile;
    $[161] = isPageLoading;
    $[162] = pendingFocusChatId;
    $[163] = pinnedChats;
    $[164] = rowProps;
    $[165] = saveTitle;
    $[166] = t71;
  } else {
    t71 = $[166];
  }
  let t72;
  if ($[167] !== isHistoryHidden || $[168] !== t71) {
    t72 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t71}</div>;
    $[167] = isHistoryHidden;
    $[168] = t71;
    $[169] = t72;
  } else {
    t72 = $[169];
  }
  let t73;
  if ($[170] !== t70 || $[171] !== t72) {
    t73 = <SidebarContent className="overflow-hidden pb-2">{t70}{t72}</SidebarContent>;
    $[170] = t70;
    $[171] = t72;
    $[172] = t73;
  } else {
    t73 = $[172];
  }
  const t74 = !selectedOrganization || selectedOrganization.isChat ? organization : selectedOrganization;
  const t75 = !selectedOrganization || selectedOrganization.isChat;
  let t76;
  if ($[173] !== isOrgLoading || $[174] !== t74 || $[175] !== t75) {
    t76 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t74} isLoading={isOrgLoading} isChatPlanOrg={t75} /></div>;
    $[173] = isOrgLoading;
    $[174] = t74;
    $[175] = t75;
    $[176] = t76;
  } else {
    t76 = $[176];
  }
  const t77 = user?.name ?? "User";
  let t78;
  if ($[177] !== user?.name) {
    t78 = user?.name?.split(" ").map(_temp6).join("").toUpperCase().slice(0, 2) ?? "U";
    $[177] = user?.name;
    $[178] = t78;
  } else {
    t78 = $[178];
  }
  let t79;
  if ($[179] !== t78) {
    t79 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t78}</span></div>;
    $[179] = t78;
    $[180] = t79;
  } else {
    t79 = $[180];
  }
  const t80 = user?.name;
  let t81;
  if ($[181] !== t80) {
    t81 = <span className="truncate font-semibold">{t80}</span>;
    $[181] = t80;
    $[182] = t81;
  } else {
    t81 = $[182];
  }
  const t82 = user?.email;
  let t83;
  if ($[183] !== t82) {
    t83 = <span className="truncate text-xs text-muted-foreground">{t82}</span>;
    $[183] = t82;
    $[184] = t83;
  } else {
    t83 = $[184];
  }
  let t84;
  if ($[185] !== t81 || $[186] !== t83) {
    t84 = <div className="grid flex-1 text-left text-sm leading-tight">{t81}{t83}</div>;
    $[185] = t81;
    $[186] = t83;
    $[187] = t84;
  } else {
    t84 = $[187];
  }
  let t85;
  if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[188] = t85;
  } else {
    t85 = $[188];
  }
  let t86;
  if ($[189] !== t77 || $[190] !== t79 || $[191] !== t84) {
    t86 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t77} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t79}{t84}{t85}</SidebarMenuButton></DropdownMenuTrigger>;
    $[189] = t77;
    $[190] = t79;
    $[191] = t84;
    $[192] = t86;
  } else {
    t86 = $[192];
  }
  let t87;
  let t88;
  if ($[193] === Symbol.for("react.memo_cache_sentinel")) {
    t87 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llm-gateway.agenzo.com/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t88 = <DropdownMenuSeparator />;
    $[193] = t87;
    $[194] = t88;
  } else {
    t87 = $[193];
    t88 = $[194];
  }
  let t89;
  if ($[195] !== toggleTheme) {
    t89 = event_0 => {
      event_0.preventDefault();
      toggleTheme();
    };
    $[195] = toggleTheme;
    $[196] = t89;
  } else {
    t89 = $[196];
  }
  let t90;
  if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = <span>Theme</span>;
    $[197] = t90;
  } else {
    t90 = $[197];
  }
  let t91;
  if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <div onClick={_temp7} onKeyDown={_temp8}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[198] = t91;
  } else {
    t91 = $[198];
  }
  let t92;
  if ($[199] !== t89) {
    t92 = <DropdownMenuItem className="justify-between gap-3" onSelect={t89}>{t90}{t91}</DropdownMenuItem>;
    $[199] = t89;
    $[200] = t92;
  } else {
    t92 = $[200];
  }
  let t93;
  if ($[201] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = <DropdownMenuSeparator />;
    $[201] = t93;
  } else {
    t93 = $[201];
  }
  let t94;
  if ($[202] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = <LogOut className="mr-2 h-4 w-4" />;
    $[202] = t94;
  } else {
    t94 = $[202];
  }
  let t95;
  if ($[203] !== logout) {
    t95 = <DropdownMenuItem onClick={logout}>{t94}Log out</DropdownMenuItem>;
    $[203] = logout;
    $[204] = t95;
  } else {
    t95 = $[204];
  }
  let t96;
  if ($[205] !== t92 || $[206] !== t95) {
    t96 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t87}{t88}{t92}{t93}{t95}</DropdownMenuContent>;
    $[205] = t92;
    $[206] = t95;
    $[207] = t96;
  } else {
    t96 = $[207];
  }
  let t97;
  if ($[208] !== t86 || $[209] !== t96) {
    t97 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t86}{t96}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[208] = t86;
    $[209] = t96;
    $[210] = t97;
  } else {
    t97 = $[210];
  }
  let t98;
  if ($[211] !== t76 || $[212] !== t97) {
    t98 = <SidebarFooter>{t76}{t97}</SidebarFooter>;
    $[211] = t76;
    $[212] = t97;
    $[213] = t98;
  } else {
    t98 = $[213];
  }
  let t99;
  if ($[214] !== handleChatSelect || $[215] !== isSearchOpen || $[216] !== onNewChat) {
    t99 = <ChatSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} onNewChat={onNewChat} onChatSelect={handleChatSelect} />;
    $[214] = handleChatSelect;
    $[215] = isSearchOpen;
    $[216] = onNewChat;
    $[217] = t99;
  } else {
    t99 = $[217];
  }
  let t100;
  if ($[218] !== t23 || $[219] !== t67 || $[220] !== t73 || $[221] !== t98 || $[222] !== t99) {
    t100 = <Sidebar collapsible="icon" className={t23}>{t67}{t73}{t98}{t99}</Sidebar>;
    $[218] = t23;
    $[219] = t67;
    $[220] = t73;
    $[221] = t98;
    $[222] = t99;
    $[223] = t100;
  } else {
    t100 = $[223];
  }
  return t100;
};
function _temp2(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}
function _temp3(chat_2) {
  return chat_2.pinned;
}
function _temp4(chat_3) {
  return !chat_3.pinned;
}
function _temp5(group) {
  return group.chats.length > 0;
}
function _temp6(n) {
  return n[0];
}
function _temp7(event_1) {
  return event_1.stopPropagation();
}
function _temp8(event_2) {
  return event_2.stopPropagation();
}